import { Attachment, Send } from '@mui/icons-material';
import { Button, Drawer, IconButton } from '@mui/material';
import { Loader } from 'components/common/Loader';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import FormInput from 'components/FormInput';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Comment, commentActionEnum } from 'redux/conductInspectionSlice';
import { Project } from 'redux/projectSlice';
import { AttachmentHolder } from '../AttachmentHolder';
import { AttachmentMenu } from '../AttachmentMenu';
import { ChatHeader } from '../ChatHeader';
import { CommentItem } from '../CommentItem';
import { CommentOptions } from '../CommentOptions';

import './ChatDrawer.scss';

export type ChatDrawerProps = {
  project: Project;
  uploading?: boolean;
  taskStatus?: WorkFlowStatus;
  submittingComment?: boolean;
  description: React.ReactNode;
  chatInput: string;
  commentOption: string;
  currentStatus: commentActionEnum | null;
  comments: Comment[];
  imageBase64?: string;
  uploadedFile?: File | null;
  isInspectorDoerSame?: boolean;
  onClose: () => void;
  onCommentOptionChange: (option: string) => void;
  onChatInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSendComment: () => void;
  onAttachmentHolderClose: () => void;
  onImageCapture: (image: string) => void;
  onFileUpload: (file: File) => void;
};

export const ChatDrawer: React.FC<ChatDrawerProps> = ({
  project,
  uploading,
  taskStatus,
  submittingComment,
  comments,
  description,
  chatInput,
  commentOption,
  currentStatus,
  imageBase64,
  uploadedFile,
  isInspectorDoerSame,
  onClose,
  onCommentOptionChange,
  onChatInputChange,
  onSendComment,
  onAttachmentHolderClose,
  onImageCapture,
  onFileUpload,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<any>(null);

  const handleClose = () => {
    setAnchorEl(null);
    onClose();
  };

  const handleShowAttachmentMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.target);
  };

  const handleCloseAttachmentMenu = () => setAnchorEl(null);

  return (
    <Drawer
      className="chat-drawer"
      open
      anchor="bottom"
      PaperProps={{
        sx: {
          margin: '0 10px',
          borderRadius: 1,
          height: '85%',
        },
        className: 'chat-window',
      }}
      onClose={handleClose}
    >
      <div className="d-flex flex-column w-100 h-100">
        {uploading && <Loader />}
        <ChatHeader description={description} onClose={onClose} />
        <div className="d-flex flex-column p-2 flex-grow-1 overflow-auto">
          <ul className="d-flex flex-column chat-list m-0 p-2 flex-grow-1 overflow-auto mb-1">
            {comments.map((c) => (
              <CommentItem key={c._id} comment={c} project={project} />
            ))}
          </ul>
          {taskStatus !== WorkFlowStatus.CLOSE && (
            <>
              <CommentOptions
                project={project}
                currentStatus={currentStatus}
                commentOption={commentOption}
                isInspectorDoerSame={isInspectorDoerSame}
                onCommentOptionChange={onCommentOptionChange}
              />
              <div className="drawer-footer d-flex w-100 my-1 align-items-center flex-row">
                <div className="position-relative flex-grow-1">
                  <FormInput
                    className="flex-grow-1 mb-0 change-input shadow-none"
                    name="chatInput"
                    value={chatInput}
                    placeholder={t('placehold_add_comments')}
                    disabled={submittingComment || !commentOption}
                    onChange={onChatInputChange}
                  />
                  {(imageBase64 || uploadedFile) && (
                    <AttachmentHolder
                      image={imageBase64}
                      file={uploadedFile}
                      onClose={onAttachmentHolderClose}
                    />
                  )}
                </div>
                <IconButton
                  disabled={submittingComment || !commentOption}
                  className="mx-1"
                  onClick={handleShowAttachmentMenu}
                >
                  <Attachment />
                </IconButton>
                <Button
                  className="btn_send"
                  variant="contained"
                  disabled={chatInput.length === 0 || submittingComment}
                  endIcon={<Send sx={{ color: '#fff' }} />}
                  onClick={onSendComment}
                >
                  {t('btn_send')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      <AttachmentMenu
        anchorEl={anchorEl}
        onFileUpload={onFileUpload}
        onImageCapture={onImageCapture}
        onClose={handleCloseAttachmentMenu}
      />
    </Drawer>
  );
};
